import React from 'react';
import { useTranslation } from 'react-i18next';
import { Anchor, Text, UnstyledButton } from '@/shared/ui';
import { EnvelopeClosedIcon, ExternalLinkIcon, GlobeIcon } from '@/shared/ui/radix-icons';
import { BuildingIcon } from '@/shared/ui/components/basic/icons';
import type { Visit } from '@services/requests/housien-models/Visit';
import { buildGoogleMapsLink } from '@/shared/utils/maps-link';
import { buildWhatsAppLink } from '@/shared/utils/whatsapp-link';
import { formatPhoneDisplay } from '@/shared/utils/format-phone';
import { SectionCard, ClientAvatar, BrokerAvatar } from './VisitDetailPrimitives';

type VisitProperty = NonNullable<Visit['properties']>[number];

interface PropertyCardProps {
  prop?: VisitProperty;
  propertyIds?: string[];
  onNavigate: (id: string) => void;
  /** Extra classes on the card surface, e.g. `flex-1` to fill the column height. */
  className?: string;
}

/**
 * Property summary card with clickable link to property detail and Maps navigation.
 */
export function PropertyCard({ prop, propertyIds, onNavigate, className }: PropertyCardProps) {
  const { t } = useTranslation('@pages/agencies');
  const propertyId = prop?.id ?? propertyIds?.[0];
  const desc = prop
    ? (prop as Record<string, unknown>).description as Record<string, string> | undefined
    : undefined;
  const title = desc?.headline || t('visits.detail.property', 'Imóvel');
  const address = buildAddress(prop);
  const mapsLink = buildGoogleMapsLink(address);

  return (
    <SectionCard
      title={t('visits.detail.property', 'Imóvel')}
      titleCase="sentence"
      icon={<BuildingIcon width={14} height={14} />}
      className={className}
    >
      <div
        className={propertyId ? 'cursor-pointer group' : ''}
        onClick={() => propertyId && onNavigate(propertyId)}
        data-testid="visit-property-link"
      >
        <div className="flex items-center justify-between">
          <Text className="text-sm font-semibold text-(--seed-base) group-hover:underline">
            {title}
          </Text>
          {propertyId && (
            <ExternalLinkIcon width={12} height={12} className="text-(--text-tertiary) opacity-0 group-hover:opacity-100" />
          )}
        </div>
        {address && (
          <Text className="mt-[var(--space-1)] text-[length:var(--text-meta)] text-(--text-secondary) leading-relaxed">
            {address}
          </Text>
        )}
        {!prop && propertyIds?.[0] && (
          <Text className="text-[length:var(--text-meta)] text-(--text-tertiary) mt-[var(--space-1)]">
            ID: {propertyIds[0]}
          </Text>
        )}
      </div>
      {mapsLink && (
        <Anchor
          href={mapsLink}
          target="_blank"
          rel="noopener noreferrer"
          onClick={(e) => e.stopPropagation()}
          data-testid="visit-maps-link"
          underline="never"
          className="inline-flex items-center gap-[var(--space-2)] mt-[var(--space-3)] px-[var(--space-3)] py-[var(--space-1-5)] text-[length:var(--text-meta)] font-medium text-(--seed-base) border border-(--seed-base) rounded-(--radius-md) hover:bg-[color-mix(in_srgb,var(--seed-base)_8%,transparent)] transition-[background-color] duration-[var(--transition-standard)]"
        >
          <GlobeIcon width={12} height={12} />
          {t('visits.detail.navigate', 'Ver no Maps')}
        </Anchor>
      )}
    </SectionCard>
  );
}

interface ClientCardProps {
  clientName?: string;
  clientPhone?: string;
  clientId?: string;
  onNavigate: (id: string) => void;
}

/**
 * Client summary card with avatar, navigation link, and WhatsApp quick contact.
 */
export function ClientCard({ clientName, clientPhone, clientId, onNavigate }: ClientCardProps) {
  const { t } = useTranslation('@pages/agencies');
  const whatsappLink = clientPhone ? buildWhatsAppLink(clientPhone) : '';
  // The link keeps the raw digits; only the label is punctuated.
  const phoneLabel = formatPhoneDisplay(clientPhone);

  return (
    <SectionCard title={t('visits.detail.client', 'Cliente')} titleCase="sentence">
      <div className="flex items-center gap-[var(--space-3)]">
        <ClientAvatar name={clientName} />
        <div className="flex-1 min-w-0">
          <UnstyledButton
            type="button"
            onClick={() => clientId && onNavigate(clientId)}
            className={clientId
              ? 'text-sm font-semibold text-(--text-primary) hover:text-(--seed-base) hover:underline truncate cursor-pointer'
              : 'text-sm font-semibold text-(--text-primary) truncate'}
            data-testid="visit-client-link"
          >
            {clientName || '—'}
          </UnstyledButton>
          {clientPhone && (
            <div className="flex items-center gap-[var(--space-2)] mt-[var(--space-0-5)]">
              <Anchor
                href={`tel:${clientPhone}`}
                underline="never"
                className="text-[length:var(--text-meta)] text-(--text-secondary) hover:text-(--seed-base)"
                data-testid="visit-client-phone"
              >
                {phoneLabel}
              </Anchor>
              {whatsappLink && (
                <Anchor
                  href={whatsappLink}
                  target="_blank"
                  rel="noopener noreferrer"
                  data-testid="visit-whatsapp-link"
                  underline="never"
                  className="inline-flex items-center gap-(--space-1) text-[length:var(--text-meta)] font-medium text-(--status-success-text) hover:underline"
                >
                  WhatsApp
                </Anchor>
              )}
            </div>
          )}
        </div>
      </div>
    </SectionCard>
  );
}

interface BrokerCardProps {
  brokerName?: string;
  ownerEmail?: string;
  /** Extra classes on the card surface, e.g. `flex-1` to fill the column height. */
  className?: string;
}

/**
 * Broker summary card with avatar, email link, and contact actions.
 */
export function BrokerCard({ brokerName, ownerEmail, className }: BrokerCardProps) {
  const { t } = useTranslation('@pages/agencies');
  return (
    <SectionCard title={t('visits.detail.broker', 'Corretor')} titleCase="sentence" className={className}>
      <div className="flex items-center gap-[var(--space-3)]">
        <BrokerAvatar name={brokerName} />
        <div className="flex-1 min-w-0">
          <Text className="text-sm font-semibold text-(--text-primary) truncate">
            {brokerName || '—'}
          </Text>
          {ownerEmail && (
            <Anchor
              href={`mailto:${ownerEmail}`}
              underline="never"
              className="flex items-center gap-[var(--space-1)] mt-[var(--space-0-5)] text-[length:var(--text-meta)] text-(--text-secondary) hover:text-(--seed-base)"
              data-testid="visit-broker-email"
            >
              <EnvelopeClosedIcon width={11} height={11} />
              {ownerEmail}
            </Anchor>
          )}
        </div>
      </div>
    </SectionCard>
  );
}

/**
 * Notes card displaying the visit observations.
 */
export function NotesCard({ notes }: { notes: string }) {
  const { t } = useTranslation('@pages/agencies');
  return (
    <SectionCard title={t('visits.detail.notes', 'Observações')} titleCase="sentence">
      <Text className="text-sm leading-relaxed text-(--text-primary) whitespace-pre-wrap">
        {notes}
      </Text>
    </SectionCard>
  );
}

/**
 * Builds a human-readable address from a visit property.
 */
function buildAddress(prop?: VisitProperty): string {
  if (!prop) return '';
  const loc = (prop as Record<string, unknown>).location as Record<string, string> | undefined;
  if (!loc) return '';
  const streetFull = loc.street && loc.number ? `${loc.street}, ${loc.number}` : loc.street;
  return [streetFull, loc.neighborhood, loc.city, loc.state].filter(Boolean).join(', ');
}
